// lib
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { isEqual } from 'lodash'

import {
  THEME_BACKGROUND,
  withDefaults
} from '@aytm/app/stats/page/question-views/components/word-cloud/constants/config'
import { withPresetReset } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/presetFields'

import { FONTS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/fonts'

import {
  BASIC_MASKS,
  SVG_PATHS,
  SVG_PATHS_TRANSFORM
} from '@aytm/app/stats/page/question-views/components/word-cloud/constants/masks'

import { GRADIENTS, MASK_OPTIONS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/sync'

import { optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

// components
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Slider } from 'primereact/slider'
import { Dropdown } from 'primereact/dropdown'

import { MultiSelect } from 'primereact/multiselect'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import ColorLab from '@aytm/app/stats/page/question-views/components/word-cloud/ColorLab'

import MaskUploader from '@aytm/app/stats/page/question-views/components/word-cloud/components/MaskUploader'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/word-cloud/styles/Controls.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const ORIENTAION_OPTIONS = [
  { text: 'Portrait', value: 'v' },
  { text: 'Landscape', value: 'h' }
]

// While a custom mask holds the live redraw, angle, orientation and slider edits wait in local state for Update.
const HELD_FIELDS = ['angles', 'orientation', 'maxWords', 'rangeStart', 'rangeEnd']

const rotateElement = (node, x, y, isShift) => {
  const rect = node.getBoundingClientRect()

  const nx = x - (rect.left + rect.width / 2)
  const ny = rect.top + rect.height / 2 - y

  const angle = isShift
    ? (Math.round(((Math.atan2(ny, -nx) * 180) / Math.PI - 90) / 45) * 45 + 360) % 360
    : ((Math.atan2(ny, -nx) * 180) / Math.PI + 270) % 360

  node.setAttribute('transform', `translate(20, 20) rotate(${angle})`)

  return angle
}

const Controls = ({
  model,
  data,
  words,
  config,
  disabled,
  isSurveyGuest,
  onParamsChange,
  onMaskUploaded,
  onReset,
  onSave
}) => {
  const { id, changed, useRawData } = model
  const { rawData, colorThemes, customPalettes } = data

  const [state, setState] = useState(withDefaults(config))

  const [updateNeeded, setUpdateNeeded] = useState(!['', '-1'].includes(config.mask) || '-1' != config.customMaskId)

  const stateRef = useRef({})
  stateRef.current = state

  const pointer = useRef({ x: 0, y: 0 })

  const delayTimerRef = useRef(null)

  // Track active drag handlers for cleanup on unmount
  const dragCleanupRef = useRef(null)

  const orientationRef = useRef(null)
  orientationRef.current = state.orientation

  const isChanged = useMemo(
    () =>
      state.maxWords != config.maxWords ||
      state.rangeStart != config.rangeStart ||
      state.rangeEnd != config.rangeEnd ||
      state.angles != config.angles ||
      state.orientation != config.orientation ||
      state.fontFamily != config.fontFamily ||
      state.colorStyle != config.colorStyle ||
      state.spell != config.spell ||
      state.caps != config.caps ||
      state.mergeWords != config.mergeWords ||
      state.twoWords != config.twoWords ||
      state.stop1 != config.stop1 ||
      state.stop2 != config.stop2 ||
      state.stop3 != config.stop3 ||
      state.stop4 != config.stop4 ||
      state.stop5 != config.stop5 ||
      state.blackList != config.blackList ||
      state.mask != config.mask ||
      state.customMaskId != config.customMaskId ||
      state.customMaskPath != config.customMaskPath,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state,
      config.maxWords,
      config.rangeStart,
      config.rangeEnd,
      config.angles,
      config.orientation,
      config.fontFamily,
      config.colorStyle,
      config.spell,
      config.caps,
      config.mergeWords,
      config.twoWords,
      config.stop1,
      config.stop2,
      config.stop3,
      config.stop4,
      config.stop5,
      config.blackList,
      config.mask,
      config.customMaskId,
      config.customMaskPath
    ]
  )

  const fontFamiliesOptions = useMemo(
    () =>
      FONTS.map(item => {
        return {
          text: `Font: <b style='font-family: ${item[0]};'>${item[1]}</b>`,
          value: item[0],
          label: `<b style='font-family: ${item[0]};'>${item[1]}</b>`
        }
      }),
    []
  )

  const colorsPlaceholder = useMemo(() => {
    let placeholder = ''
    placeholder =
      'gradient' == state.colorStyle
        ? `<span class='${style.color}' style='background-color: ${state.colors[0]};'></span>` +
          `<span class='${style['color-gradient']}' style='background: linear-gradient(to right, ${state.colors[0]} 0%, ${state.colors[1]} 100%);'>` +
          `</span><span class='${style.color}' style='background-color: ${state.colors[1]};'></span>`
        : state.colors.map(color => `<span class='${style.color}' style='background-color: ${color};'></span>`).join('')

    return `${placeholder}<span class='${style.color} ${style['color-bg']}' style='background-color: ${state.background};'></span>`
  }, [state.colorStyle, state.colors, state.background])

  const colorsOptions = useMemo(
    () =>
      'gradient' == state.colorStyle
        ? GRADIENTS.map((item, index) => {
            return {
              text:
                `<span class='${style.color}' style='background-color: ${item[0][0]};'></span>` +
                `<span class='${style['color-gradient']}' style='background: linear-gradient(to right, ${item[0][0]} 0%, ${item[0][1]} 100%);'></span>` +
                `<span class='${style.color}' style='background-color: ${item[0][1]};'></span>` +
                `<span class='${style.color} ${style['color-bg']}' style='background-color: ${item[1]};'></span>`,
              value: `gradient-${index + 1}`
            }
          })
        : [
            ...colorThemes.map((item, index) => {
              return {
                text: item
                  .slice(0, 7)
                  .map(color => `<span class='${style.color}' style='background-color: ${color};'></span>`)
                  .concat([
                    `<span class='${style.color} ${style['color-bg']}' style='background-color: ${THEME_BACKGROUND};'></span>`
                  ])
                  .join(''),
                value: index + 1
              }
            }),
            ...customPalettes.map(item => {
              return {
                text: item.data.colors
                  .map(color => `<span class='${style.color}' style='background-color: ${color};'></span>`)
                  .concat([
                    `<span class='${style.color} ${style['color-bg']}' style='background-color: ${THEME_BACKGROUND};'></span>`
                  ])
                  .join(''),
                value: `custom-${item.id}`
              }
            })
          ],
    [state.colorStyle, colorThemes, customPalettes]
  )

  const cleanUpValue = useMemo(() => {
    const stops = []
    if (1 == state.stop1) stops.push('stop1')
    if (1 == state.stop2) stops.push('stop2')
    if (1 == state.stop3) stops.push('stop3')
    if (1 == state.stop4) stops.push('stop4')
    if (1 == state.stop5) stops.push('stop5')

    return [...stops, ...state.blackList]
  }, [state.stop1, state.stop2, state.stop3, state.stop4, state.stop5, state.blackList])

  const cleanUpOptions = useMemo(
    () => [
      {
        text: 'Hide common english words:',
        items: [
          {
            text: '#1. "the", "we", "if", etc.',
            value: 'stop1'
          },
          {
            text: '#2. "can", "more", "want", etc.',
            value: 'stop2'
          },
          {
            text: '#3. "ever", "several", "top", etc.',
            value: 'stop3'
          },
          {
            text: '#4. Hide numbers.',
            value: 'stop4'
          },
          {
            text: '#5. Hide one-letter words.',
            value: 'stop5'
          }
        ]
      },
      {
        text: 'Hide other words:',
        items: [
          ...state.blackList.sort().map(item => {
            return {
              text: item,
              value: item
            }
          }),
          ...words
            .filter(item => !state.blackList.includes(item.text))
            .sort((a, b) => (state.mergeWords ? b.times - a.times : b.f - a.f))
            .map(item => {
              return {
                text: `${item.text}${0 < item.f && !state.mergeWords ? ` (${item.f})` : ''}${
                  0 < item.times && state.mergeWords ? ` (${item.times})` : ''
                }`,
                value: item.text
              }
            })
        ]
      }
    ],
    [words, state.mergeWords, state.blackList]
  )

  const masksOptions = useMemo(
    () => [
      {
        text: 'No mask',
        value: '',
        label: 'No mask'
      },
      ...MASK_OPTIONS.map(item => {
        return {
          text: item[1],
          value: item[0],
          label: item[1]
        }
      }),
      {
        text: 'Custom...',
        value: '-1',
        label: 'Custom...'
      }
    ],
    []
  )

  const labelTemplate = value => <span>{value}</span>

  const itemTemplate = option => <span dangerouslySetInnerHTML={{ __html: option.text }} />
  // the two dropdowns that would otherwise read as a bare value, named in front of it
  const fontValueTemplate = (option, _props) =>
    option ? (
      <>
        <span className={style['dropdown-name']}>Font:</span>
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }} />
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }} />
    )

  const orientationValueTemplate = (option, _props) =>
    option ? (
      <span dangerouslySetInnerHTML={{ __html: `Orientation: ${option.label || option.text}` }} />
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }} />
    )

  const maskValueTemplate = (option, _props) =>
    option ? (
      <>
        {BASIC_MASKS.includes(option.value) ? (
          <span className={style[`mask-${option.value}`]} />
        ) : SVG_PATHS[`${option.value}_${orientationRef.current}`] ? (
          <svg
            className={classNames(style['mask-svg'], style[`mask-svg-${option.value}`])}
            width="20"
            height="20"
            viewBox="0 0 20 20"
          >
            <g transform={SVG_PATHS_TRANSFORM[`${option.value}_${orientationRef.current}`]}>
              <path
                d={SVG_PATHS[`${option.value}_${orientationRef.current}`]
                  .map(p => `${p[0]} ${p.slice(1).join(',')}`.toUpperCase())
                  .join(' ')}
                fill="#223d64"
              ></path>
            </g>
          </svg>
        ) : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }} />
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }} />
    )

  const colorsItemTemplate = option => (
    <span
      className={classNames(style.mode, style[`mode-${state.colorStyle}`])}
      dangerouslySetInnerHTML={{ __html: option.text }}
    />
  )
  const colorsValueTemplate = (option, _props) =>
    option ? (
      <span
        className={classNames(style.mode, style[`mode-${state.colorStyle}`])}
        dangerouslySetInnerHTML={{ __html: option.label || option.text }}
      />
    ) : (
      <span
        className={classNames(style.mode, style[`mode-${state.colorStyle}`])}
        dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}
      />
    )

  // A preset-owned change means the cloud is that preset no longer, and the serializer skips
  // those fields until the config says custom. Record the switch in local state as the change
  // happens, so every commit path hands the block a config that already says so -- including the
  // Update button, which is the only commit there is while a custom mask holds the live redraw.
  const applyState = useCallback(params => {
    const next = Object.assign({}, stateRef.current, withPresetReset(params))

    setState(next)

    return next
  }, [])

  // A control that writes at once sends only its own change, and the config it brings back would
  // replace local state -- so the held edits are set aside here and laid back over that config, still
  // waiting for Update. Only edits that differ from the config are held.
  const heldRef = useRef(null)

  const writeNow = useCallback(
    params => {
      const held = updateNeeded ? HELD_FIELDS.filter(field => !isEqual(stateRef.current[field], config[field])) : []

      heldRef.current = held.length
        ? Object.fromEntries([...held, 'currentPresetId'].map(field => [field, stateRef.current[field]]))
        : null

      onParamsChange(withPresetReset(params))
    },
    [updateNeeded, config, onParamsChange]
  )

  const onAngleClick = useCallback(
    (event, position) => {
      const { x, y } = pointer.current

      if (Math.abs(event.clientX - x) < 1 && Math.abs(event.clientY - y) < 1) {
        const newAngles =
          position <= stateRef.current.angles.length
            ? stateRef.current.angles.filter((_, index) => index != position - 1)
            : [...stateRef.current.angles, 0]

        const next = applyState({ angles: newAngles })

        if (!updateNeeded) onParamsChange(next)
      }
    },
    [updateNeeded, applyState, onParamsChange]
  )

  const onAngleMouseDown = useCallback(
    (event, position) => {
      event.preventDefault()

      // Abort any in-flight drag (rapid re-mousedown, alt-tab mid-drag) before replacing the ref
      dragCleanupRef.current?.()

      const controller = new AbortController()
      const { signal } = controller

      pointer.current = { x: event.clientX, y: event.clientY }

      const newAngles = [...stateRef.current.angles]

      let isShift = event.metaKey || event.shiftKey

      let originalX = event.clientX
      let originalY = event.clientY

      const node = event.currentTarget

      const handleKeyDown = e => {
        if (isShift) return
        isShift = e.metaKey || e.shiftKey
        if (isShift) newAngles[position - 1] = rotateElement(node, originalX, originalY, isShift)
      }
      const handleKeyUp = e => {
        if (!isShift) return
        isShift = !e.metaKey && !e.shiftKey
        if (!isShift) newAngles[position - 1] = rotateElement(node, originalX, originalY, isShift)
      }
      const handleMouseMove = e => {
        originalX = e.clientX
        originalY = e.clientY
        newAngles[position - 1] = rotateElement(node, e.clientX, e.clientY, isShift)
      }
      const commitAndCleanup = () => {
        controller.abort()
        dragCleanupRef.current = null

        const next = applyState({ angles: newAngles })

        if (!updateNeeded) onParamsChange(next)
      }

      document.addEventListener('keydown', handleKeyDown, { signal })
      document.addEventListener('keyup', handleKeyUp, { signal })
      document.addEventListener('mousemove', handleMouseMove, { signal })
      document.addEventListener('mouseup', commitAndCleanup, { signal, once: true })
      // Safety net: commit and tear down if the window loses focus mid-drag (no mouseup will arrive)
      window.addEventListener('blur', commitAndCleanup, { signal, once: true })

      dragCleanupRef.current = () => {
        controller.abort()
        dragCleanupRef.current = null
      }
    },
    [updateNeeded, applyState, onParamsChange]
  )

  const onColorChange = useCallback(
    ({ mode, colors, background }) => {
      applyState({ colorStyle: mode, colors, background }) // the popup names the coloring algorithm `mode`

      if (delayTimerRef.current) clearTimeout(delayTimerRef.current)
      delayTimerRef.current = setTimeout(() => {
        onParamsChange(stateRef.current)
      }, 400)
    },
    [applyState, onParamsChange]
  )

  const onColorsChange = useCallback(
    event => {
      let colors = []
      let background = THEME_BACKGROUND

      if (String(event.value).startsWith('gradient-')) {
        const id = parseInt(event.value.replace('gradient-', ''), 10)
        colors = GRADIENTS[id - 1][0]
        background = GRADIENTS[id - 1][1]
      } else if (String(event.value).startsWith('custom-')) {
        const id = parseInt(event.value.replace('custom-', ''), 10)
        colors = customPalettes.find(v => v.id == id).data.colors
      } else {
        colors = colorThemes[event.value - 1].slice(0, 7)
      }

      if ('gradient' == state.colorStyle) {
        colors = [colors.at(0), colors.at(-1)]
      }

      writeNow({ colors, background })
    },
    [state.colorStyle, colorThemes, customPalettes, writeNow]
  )

  const onCleanUpChange = useCallback(
    event => {
      const stops = {
        stop1: event.value.includes('stop1') ? 1 : 0,
        stop2: event.value.includes('stop2') ? 1 : 0,
        stop3: event.value.includes('stop3') ? 1 : 0,
        stop4: event.value.includes('stop4') ? 1 : 0,
        stop5: event.value.includes('stop5') ? 1 : 0
      }

      const blackList = event.value.filter(value => !['stop1', 'stop2', 'stop3', 'stop4', 'stop5'].includes(value))

      writeNow({ ...stops, blackList })
    },
    [writeNow]
  )

  const onMaskChange = useCallback(
    event => {
      if ('-1' == event.value) {
        setState(Object.assign({}, state, { mask: '-1' }))
      } else {
        writeNow({ mask: event.value, customMaskId: '-1' })
      }
    },
    [state, writeNow]
  )

  const onMaskCancel = useCallback(
    () => setState(Object.assign({}, state, { mask: config.mask })),
    [state, config.mask]
  )

  const onSliderChange = useCallback(
    params => {
      applyState(params)

      if (delayTimerRef.current) clearTimeout(delayTimerRef.current)
      delayTimerRef.current = setTimeout(() => {
        if (!updateNeeded) onParamsChange(stateRef.current)
      }, 400)
    },
    [updateNeeded, applyState, onParamsChange]
  )

  const onOrientationChange = useCallback(
    event => {
      applyState({ orientation: event.value })

      if (!updateNeeded) writeNow({ orientation: event.value })
    },
    [updateNeeded, applyState, writeNow]
  )

  const onShuffle = () => writeNow({ randIndex: Math.floor(Math.random() * 750) })

  useEffect(
    () => {
      const held = heldRef.current
      heldRef.current = null

      setState(held ? { ...config, ...held } : config)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      config.maxWords,
      config.rangeStart,
      config.rangeEnd,
      config.angles,
      config.orientation,
      config.fontFamily,
      config.colorStyle,
      config.spell,
      config.caps,
      config.mergeWords,
      config.twoWords,
      config.stop1,
      config.stop2,
      config.stop3,
      config.stop4,
      config.stop5,
      config.blackList,
      config.mask,
      config.customMaskId,
      config.customMaskPath
    ]
  )
  useEffect(
    () => setUpdateNeeded(!['', '-1'].includes(state.mask) || '-1' != state.customMaskId),
    [state.mask, state.customMaskId]
  )

  useEffect(
    () => () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current)

      // Clean up drag event listeners if component unmounts while dragging
      if (dragCleanupRef.current) dragCleanupRef.current()
    },
    []
  )

  const renderAngle = (position, active = false, value = 0) => (
    <svg
      key={`angle-${position}`}
      data-testid={`stat-wordcloud-${id}-angle-${position}`}
      width="40"
      height="40"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient
          id={`wc-angle-gradient-bg-${id}-${position}`}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
          spreadMethod="pad"
        >
          <stop offset="0%" stopColor="#ffffff"></stop>
          <stop offset="100%" stopColor="#f2f2f2"></stop>
        </linearGradient>
        <linearGradient id={`wc-angle-gradient-${id}-${position}`} x1="0%" y1="0%" x2="0%" y2="100%" spreadMethod="pad">
          <stop offset="0%" stopColor="#4db3de"></stop>
          <stop offset="100%" stopColor="#2890cb"></stop>
        </linearGradient>
      </defs>
      <g
        transform={`translate(20, 20) rotate(${parseInt(String(value), 10)})`}
        onClick={event => onAngleClick(event, position)}
        onMouseDown={event => onAngleMouseDown(event, position)}
      >
        <circle cx="0" cy="0" r="17" fill={`url(#wc-angle-gradient-bg-${id}-${position})`}></circle>
        {active ? (
          <>
            <circle cx="0" cy="0" r="17" stroke="#32ade1" strokeWidth="1" fill="transparent"></circle>
            <circle cx="0" cy="-16" r="3" fill={`url(#wc-angle-gradient-${id}-${position})`}></circle>
            <path d="M-7,9L0,-8L7,9" stroke="#32ade1" strokeWidth="2" fill="transparent"></path>
            <path d="M-4,3L4,3" stroke="#32ade1" strokeWidth="2" fill="transparent"></path>
          </>
        ) : (
          <>
            <circle cx="0" cy="0" r="17" stroke="#cccccc" strokeWidth="1" fill="transparent"></circle>
            <path d="M-7,9L0,-8L7,9" stroke="#cccccc" strokeWidth="2" fill="transparent"></path>
            <path d="M-4,3L4,3" stroke="#cccccc" strokeWidth="2" fill="transparent"></path>
          </>
        )}
      </g>
    </svg>
  )

  return (
    <div className={`${style.component} ${NO_PRINT}`}>
      <div className={`${style.column} p-col-6`}>
        <div className={`${style.row} ${style['row-sliders']}`}>
          {useRawData ? null : (
            <div className="p-col-3">
              <Slider
                data-testid={`stat-wordcloud-${id}-maxwords`}
                min={1}
                max={rawData.words.length}
                value={state.maxWords || 300}
                labelTemplate={labelTemplate}
                disabled={disabled}
                onChange={disabled ? null : event => onSliderChange({ maxWords: event.value })}
              />
              <span className={style['slider-label']}>Max words</span>
            </div>
          )}
          <div className="p-col">
            <Slider
              data-testid={`stat-wordcloud-${id}-range`}
              min={1}
              max={150}
              value={[state.rangeStart || 35, state.rangeEnd || 95]}
              labelTemplate={labelTemplate}
              range
              disabled={disabled}
              onChange={
                disabled ? null : event => onSliderChange({ rangeStart: event.value[0], rangeEnd: event.value[1] })
              }
            />
            <span className={style['slider-label']}>Smallest/largest words contrast</span>
          </div>
        </div>
        <div className={style.row}>
          <div data-testid={`stat-wordcloud-${id}-angles`} className={`${style.angles} p-col`}>
            {renderAngle(1, 0 < state.angles.length, state.angles[0])}
            {renderAngle(2, 1 < state.angles.length, state.angles[1])}
            {renderAngle(3, 2 < state.angles.length, state.angles[2])}
            {renderAngle(4, 3 < state.angles.length, state.angles[3])}
            {renderAngle(5, 4 < state.angles.length, state.angles[4])}
          </div>
          <div className="p-fluid p-col-5">
            <Dropdown
              data-testid={`stat-wordcloud-${id}-orientation`}
              className="p-dropdown-rounded p-dropdown-light-blue"
              aria-label="Orientation"
              ariaLabel="Orientation"
              value={state.orientation}
              options={ORIENTAION_OPTIONS}
              optionValue="value"
              optionLabel="text"
              fixedDropdownDir="bottom"
              filter={false}
              disabled={disabled}
              itemTemplate={itemTemplate}
              valueTemplate={orientationValueTemplate}
              onChange={disabled ? null : onOrientationChange}
            />
          </div>
        </div>
        <div className={style.row}>
          <div className="p-fluid p-col">
            <Dropdown
              data-testid={`stat-wordcloud-${id}-fontfamily`}
              className="p-dropdown-rounded p-dropdown-light-blue"
              aria-label="Font"
              ariaLabel="Font"
              value={state.fontFamily}
              options={fontFamiliesOptions}
              optionValue="value"
              optionLabel="text"
              fixedDropdownDir="bottom"
              filter={false}
              disabled={disabled}
              itemTemplate={itemTemplate}
              valueTemplate={fontValueTemplate}
              onChange={disabled ? null : event => writeNow({ fontFamily: event.value })}
            />
          </div>
          {useRawData ? null : '-1' == state.mask ? (
            isSurveyGuest ? null : (
              <div className="p-col-4">
                <MaskUploader id={id} onUploaded={onMaskUploaded} onCancel={onMaskCancel} />
              </div>
            )
          ) : '-1' != state.customMaskId ? (
            <div className="p-col-4">
              <span>custom mask</span>
              <span
                data-testid={`stat-wordcloud-${id}-mask-custom-delete`}
                className={style.delete}
                aria-label="Delete custom mask"
                {...pressable(() => onMaskUploaded(null))}
              >
                <i className="icon-aytmfd-x-v2" />
              </span>
            </div>
          ) : (
            <div className="p-fluid p-col-4">
              <Dropdown
                data-testid={`stat-wordcloud-${id}-mask`}
                className="p-dropdown-rounded p-dropdown-light-blue"
                aria-label="Mask"
                ariaLabel="Mask"
                value={state.mask}
                options={masksOptions}
                optionValue="value"
                optionLabel="text"
                fixedDropdownDir="bottom"
                filter={false}
                disabled={disabled}
                itemTemplate={itemTemplate}
                valueTemplate={maskValueTemplate}
                onChange={disabled ? null : onMaskChange}
              />
            </div>
          )}
        </div>
        <div className={style.row}>
          <div className="p-fluid p-col">
            <Dropdown
              data-testid={`stat-wordcloud-${id}-colors`}
              className="p-dropdown-rounded p-dropdown-light-blue"
              placeholder={colorsPlaceholder}
              value={null}
              options={colorsOptions}
              optionValue="value"
              optionLabel="text"
              itemTemplate={colorsItemTemplate}
              valueTemplate={colorsValueTemplate}
              filter={false}
              fixedDropdownDir="bottom"
              disabled={disabled}
              onChange={disabled ? null : onColorsChange}
            />
          </div>
          {disabled ? null : (
            <div className="p-fluid p-col-4">
              <ColorLab
                mode={state.colorStyle}
                colors={state.colors}
                background={state.background}
                onChange={onColorChange}
              />
            </div>
          )}
        </div>
      </div>
      <div className={`${style.column} p-col`}>
        {useRawData ? (
          <div className={style.row}>
            {'-1' == state.mask ? (
              isSurveyGuest ? null : (
                <div className="p-col-12">
                  <MaskUploader id={id} onUploaded={onMaskUploaded} onCancel={onMaskCancel} />
                </div>
              )
            ) : '-1' != state.customMaskId ? (
              <div className="p-col-12">
                <span>custom mask</span>
                <span
                  data-testid={`stat-wordcloud-${id}-mask-custom-delete`}
                  className={style.delete}
                  aria-label="Delete custom mask"
                  {...pressable(() => onMaskUploaded(null))}
                >
                  <i className="icon-aytmfd-x-v2" />
                </span>
              </div>
            ) : (
              <div className="p-fluid p-col-12">
                <Dropdown
                  data-testid={`stat-wordcloud-${id}-mask`}
                  className="p-dropdown-rounded p-dropdown-light-blue"
                  aria-label="Mask"
                  ariaLabel="Mask"
                  value={state.mask}
                  options={masksOptions}
                  optionValue="value"
                  optionLabel="text"
                  fixedDropdownDir="bottom"
                  filter={false}
                  disabled={disabled}
                  itemTemplate={itemTemplate}
                  valueTemplate={maskValueTemplate}
                  onChange={disabled ? null : onMaskChange}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <div className={style.row}>
              <div className="p-fluid p-col-12">
                <MultiSelect
                  data-testid={`stat-wordcloud-${id}-cleanup`}
                  className="p-multiselect-rounded p-multiselect-light-blue"
                  placeholder="Text clean up settings"
                  value={cleanUpValue}
                  options={cleanUpOptions}
                  optionValue="value"
                  optionLabel="text"
                  optionGroupLabel="text"
                  optionGroupChildren="items"
                  optionGroupTemplate={optionGroupTemplate}
                  buttonLabelTemplate={() => 'Text clean up settings'}
                  panelHeaderTemplate={MultiSelectHeader}
                  panelFooterTemplate={MultiSelectFooter}
                  filter
                  filterBy="search,text"
                  filterPlaceholder="Search items"
                  disabled={disabled}
                  onChange={disabled ? null : onCleanUpChange}
                />
              </div>
            </div>
            <div className={`${style.row} ${style['row-checks']}`}>
              <div className="p-col">
                <Checkbox
                  data-testid={`stat-wordcloud-${id}-spell`}
                  className="p-checkbox-sm"
                  aria-labelledby={`stat-wordcloud-${id}-spell-label`}
                  checked={1 == state.spell}
                  disabled={disabled}
                  onChange={disabled ? null : () => writeNow({ spell: 1 == state.spell ? 0 : 1, activeWord: '' })}
                />
                <span
                  id={`stat-wordcloud-${id}-spell-label`}
                  data-testid={`stat-wordcloud-${id}-spell-label`}
                  className={classNames('p-checkbox-label p-checkbox-label-sm', { 'p-disabled': disabled })}
                  onClick={disabled ? null : () => writeNow({ spell: 1 == state.spell ? 0 : 1, activeWord: '' })}
                >
                  Fix misspells
                </span>
              </div>
              <div className="p-col">
                <Checkbox
                  data-testid={`stat-wordcloud-${id}-caps`}
                  className="p-checkbox-sm"
                  aria-labelledby={`stat-wordcloud-${id}-caps-label`}
                  checked={1 == state.caps}
                  disabled={disabled}
                  onChange={disabled ? null : () => writeNow({ caps: 1 == state.caps ? 0 : 1, activeWord: '' })}
                />
                <span
                  id={`stat-wordcloud-${id}-caps-label`}
                  data-testid={`stat-wordcloud-${id}-caps-label`}
                  className={classNames('p-checkbox-label p-checkbox-label-sm', { 'p-disabled': disabled })}
                  onClick={disabled ? null : () => writeNow({ caps: 1 == state.caps ? 0 : 1, activeWord: '' })}
                >
                  Smart capitalization
                </span>
              </div>
            </div>
            <div className={`${style.row} ${style['row-checks']}`}>
              <div className="p-col">
                <Checkbox
                  data-testid={`stat-wordcloud-${id}-mergewords`}
                  className="p-checkbox-sm"
                  aria-labelledby={`stat-wordcloud-${id}-mergewords-label`}
                  checked={1 == state.mergeWords}
                  disabled={disabled}
                  onChange={
                    disabled ? null : () => writeNow({ mergeWords: 1 == state.mergeWords ? 0 : 1, activeWord: '' })
                  }
                />
                <span
                  id={`stat-wordcloud-${id}-mergewords-label`}
                  data-testid={`stat-wordcloud-${id}-mergewords-label`}
                  className={classNames('p-checkbox-label p-checkbox-label-sm', { 'p-disabled': disabled })}
                  onClick={
                    disabled ? null : () => writeNow({ mergeWords: 1 == state.mergeWords ? 0 : 1, activeWord: '' })
                  }
                >
                  Merge similar words
                </span>
              </div>
              <div className="p-col">
                <Checkbox
                  data-testid={`stat-wordcloud-${id}-twowords`}
                  className="p-checkbox-sm"
                  aria-labelledby={`stat-wordcloud-${id}-twowords-label`}
                  checked={1 == state.twoWords}
                  disabled={disabled}
                  onChange={disabled ? null : () => writeNow({ twoWords: 1 == state.twoWords ? 0 : 1, activeWord: '' })}
                />
                <span
                  id={`stat-wordcloud-${id}-twowords-label`}
                  data-testid={`stat-wordcloud-${id}-twowords-label`}
                  className={classNames('p-checkbox-label p-checkbox-label-sm', { 'p-disabled': disabled })}
                  onClick={disabled ? null : () => writeNow({ twoWords: 1 == state.twoWords ? 0 : 1, activeWord: '' })}
                >
                  2-word phrases
                </span>
              </div>
            </div>
          </>
        )}
        <div className={`${style.row} ${style['row-buttons']}`}>
          <Button
            data-testid={`stat-wordcloud-${id}-reset`}
            className="p-button-rounded p-button-blue p-button-secondary"
            label={updateNeeded && isChanged ? 'Reset' : 'Reset wordcloud'}
            disabled={disabled || !changed}
            onClick={disabled || !changed ? null : onReset}
          />
          <Button
            data-testid={`stat-wordcloud-${id}-shuffle`}
            className="p-button-rounded p-button-blue p-button-secondary"
            label="Shuffle"
            disabled={disabled}
            onClick={disabled ? null : onShuffle}
          />
          {updateNeeded && isChanged ? (
            <Button
              data-testid={`stat-wordcloud-${id}-update`}
              className="p-button-rounded p-button-blue"
              label="Update"
              disabled={disabled}
              onClick={disabled ? null : () => onParamsChange(state)}
            />
          ) : null}
          {isSurveyGuest ? null : (
            <Button
              data-testid={`stat-wordcloud-${id}-save`}
              className="p-button-rounded p-button-blue"
              label={updateNeeded && isChanged ? 'Save' : 'Save changes'}
              disabled={disabled}
              onClick={disabled ? null : onSave}
            />
          )}
        </div>
      </div>
    </div>
  )
}

Controls.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.string,
    useRawData: PropTypes.bool,
    changed: PropTypes.bool
  }),
  data: PropTypes.object,
  words: PropTypes.array,
  config: PropTypes.shape({
    currentPresetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activeWord: PropTypes.string,
    fontFamily: PropTypes.string,
    colorStyle: PropTypes.oneOf(['adj_levels', 'levels', 'gradient', 'circle']),
    colors: PropTypes.arrayOf(PropTypes.string),
    background: PropTypes.string,
    mask: PropTypes.string,
    customMaskId: PropTypes.string,
    customMaskPath: PropTypes.string,
    angles: PropTypes.arrayOf(PropTypes.number),
    maxWords: PropTypes.number,
    scaleFunction: PropTypes.string,
    rangeStart: PropTypes.number,
    rangeEnd: PropTypes.number,
    spell: PropTypes.oneOf([0, 1]),
    caps: PropTypes.oneOf([0, 1]),
    mergeWords: PropTypes.oneOf([0, 1]),
    twoWords: PropTypes.oneOf([0, 1]),
    junk: PropTypes.oneOf([0, 1]),
    stop1: PropTypes.oneOf([0, 1]),
    stop2: PropTypes.oneOf([0, 1]),
    stop3: PropTypes.oneOf([0, 1]),
    stop4: PropTypes.oneOf([0, 1]),
    stop5: PropTypes.oneOf([0, 1]),
    blackList: PropTypes.arrayOf(PropTypes.string),
    randIndex: PropTypes.number,
    orientation: PropTypes.oneOf(['h', 'v'])
  }).isRequired,
  disabled: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  onParamsChange: PropTypes.func,
  /**
   * Called after a custom mask operation completes.
   * - Upload success → receives the server response `{ id, original: { url }, ... }`.
   * - Delete       → receives `null` (× click next to "custom mask"; see Controls.jsx call-sites).
   */
  onMaskUploaded: PropTypes.func,
  onReset: PropTypes.func,
  onSave: PropTypes.func
}

Controls.displayName = 'Controls'

export default Controls
